import React from "react";
import bgImage from "assets/images/tenant-landing-bg.jpg";
import Button from "../../components/UI/Button";
import Icon from "../../components/UI/Icon/Icon";

interface TenantLandingProps {
  /** Agent full name, e.g. "Debbie Carson" (dynamic, from tenant entry). */
  agentName?: string;
  /** Agency/company name, e.g. "Ray White Real Estate" (dynamic). */
  agentCompany?: string;
  /** Agency logo URL from the API `logo` field. Falls back to the agency name when absent. */
  logo?: string;
  /** Called when the tenant clicks "Start My Application" — reveals the existing form. */
  onStart: () => void;
}

/**
 * Branded pre-form landing screen shown as the tenant's first entry point.
 * Presentational only: renders the agent/agency/logo over a full-screen background
 * and calls `onStart` to hand off to the normal application flow.
 */
const TenantLanding: React.FC<TenantLandingProps> = ({ agentName, agentCompany, logo, onStart }) => {
  return (
    <div
      className="d-flex flex-column"
      style={{
        minHeight: "100vh",
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Centered card */}
      <div className="flex-fill d-flex align-items-center justify-content-center p-20px">
        <div
          className="w-100 bg-white radius-5px drop-shadow-xs-2"
          style={{ maxWidth: 640, overflow: "hidden" }}
        >
          {/* Navy heading band — mirrors TenantHeader */}
          <div className="bg-navy p-20px">
            <div className="fs-25px fw-bold text-white text-center">
              Apply to keep a pet in a rental property
            </div>
          </div>

          {/* White body */}
          <div className="p-30px d-flex flex-column gap-20px text-black">
            <div className="d-flex flex-column flex-md-row gap-20px justify-content-md-between">
              <div className="fs-16px d-flex gap-10px">
                <Icon name="MailSent" size={20} className="min-w-30px" />
                <div>
                  You are about to lodge a pet application to:
                  {(agentName || agentCompany) && (
                    <div className="mt-10px">
                      {agentName && <span className="fw-bold">{agentName} </span>}
                      {agentName && agentCompany && <span>from </span>}
                      {agentCompany && <span className="fw-bold">{agentCompany}</span>}
                    </div>
                  )}
                </div>
              </div>

              {logo && (
                <div
                  className="d-flex align-items-start justify-content-center"
                  style={{ width: "100%", maxWidth: 171 }}
                >
                  {/* Fit any logo into the box proportionally: full box width, height scales
                      to preserve aspect ratio so it never stretches/breaks. */}
                  <img
                    src={logo}
                    alt={agentCompany || "Agency logo"}
                    style={{ width: "100%", height: "auto", display: "block", objectFit: "contain" }}
                  />
                </div>
              )}
            </div>

            <div className="fs-16px text-grey">
              If this is not correct, please contact your agent for the correct application link.
            </div>

            <div className="d-flex justify-content-end">
              <Button
                type="button"
                text="Start My Application"
                iconName="arrowRight"
                iconPosition="right"
                onClick={onStart}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer band — mirrors TenantFooter */}
      <div className="bg-grey-1 py-30px">
        <div className="w-100 max-w-960px px-30px px-lg-0 mx-auto">
          <div className="fs-16px fw-normal text-white">Copyright &copy; {new Date().getFullYear()}</div>
        </div>
      </div>
    </div>
  );
};

export default TenantLanding;
